import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import LandingPage from './pages/LandingPage';
import ManageAppAccess from './pages/ManageAppAccess';
import SeatingChart from './pages/SeatingChart';
import AutomationScoreboard from './pages/AutomationScoreboard';
import Navbar from './components/Navbar';
import LoadingScreen from './components/LoadingScreen';
import ErrorBoundary from './components/ErrorBoundary';

function App() {
  const { isLoading, error } = useAuth0();
  const [darkMode, setDarkMode] = useState(true);
  const [darkTheme, setDarkThemeState] = useState('carbon');

  // Load dark mode preference (default to true if not set)
  useEffect(() => {
    const savedDarkModeValue = localStorage.getItem('pinnacle-darkmode');
    const savedDarkMode = savedDarkModeValue === null ? true : savedDarkModeValue === 'true';
    setDarkMode(savedDarkMode);
    if (savedDarkMode) {
      document.documentElement.classList.add('dark-mode');
    } else {
      document.documentElement.classList.remove('dark-mode');
    }

    // Load saved theme, default 'carbon'
    const savedTheme = localStorage.getItem('pinnacle-darktheme') || 'carbon';
    setDarkThemeState(savedTheme);
    document.documentElement.setAttribute('data-dark-theme', savedTheme);
  }, []);

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('pinnacle-darkmode', newMode.toString());
    document.documentElement.classList.toggle('dark-mode', newMode);
  };

  const setDarkTheme = (theme) => {
    setDarkThemeState(theme);
    localStorage.setItem('pinnacle-darktheme', theme);
    document.documentElement.setAttribute('data-dark-theme', theme);
  };

  // Clear URL parameters after Auth0 callback to prevent infinite loading
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has('code') && url.searchParams.has('state')) {
      // Auth0 callback detected - the SDK will handle the token exchange
      // After a short delay, clean up the URL if still loading
      const timeout = setTimeout(() => {
        if (window.location.search) {
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, []);

  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        padding: '20px',
        textAlign: 'center'
      }}>
        <h2 style={{ color: '#D92D20', marginBottom: '16px' }}>Authentication Error</h2>
        <p style={{ color: '#667085', marginBottom: '24px' }}>{error.message}</p>
        <button
          onClick={() => window.location.href = '/'}
          style={{
            padding: '12px 24px',
            background: '#3B82F6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Router>
      <div className="app">
        <Navbar darkMode={darkMode} toggleDarkMode={toggleDarkMode} darkTheme={darkTheme} setDarkTheme={setDarkTheme} />
        <Routes>
          <Route path="/" element={<LandingPage darkMode={darkMode} />} />
          {/* SeatingChart pulls live Graph/Azure data on render, so any
              transient failure could crash the whole page. The ErrorBoundary
              keeps the failure local to this route and offers a reload. */}
          <Route
            path="/seating-chart"
            element={
              <ErrorBoundary scope="seating chart">
                <SeatingChart />
              </ErrorBoundary>
            }
          />
          <Route
            path="/automation-scoreboard"
            element={
              <ErrorBoundary scope="automation scoreboard">
                <AutomationScoreboard />
              </ErrorBoundary>
            }
          />
          <Route
            path="/manage-app-access"
            element={
              <ErrorBoundary scope="manage app access">
                <ManageAppAccess darkMode={darkMode} />
              </ErrorBoundary>
            }
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
